import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useAnimationControls } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Layers,
  ArrowRight,
  CheckCircle,
  Zap,
  Brain,
  BarChart3,
  Users,
  Play,
  Calendar,
  Target,
  Network,
  Eye,
  Bell,
  Award,
  TrendingUp,
  GitBranch,
  MessageSquare,
  FolderKanban,
  PieChart,
  Quote,
  Star,
  Sparkles
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { Card, CardContent } from '../../ui/card';

// Planfusion Testimonial Component
interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatar: string;
  industry: string;
  color: string;
  impact: string;
}

const planfusionTestimonials: Testimonial[] = [
  {
    id: 1,
    name: 'David Anderson',
    role: 'Director of PMO',
    company: 'Tech Solutions International',
    content: 'Planfusion revolutionized project management across 45 concurrent projects. AI-powered resource allocation reduced conflicts 78%, timeline predictions achieved 94% accuracy, and project delivery improved 42%. Team collaboration increased dramatically with real-time insights.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=faces',
    industry: 'Technology',
    color: '#00CFFF',
    impact: '78% fewer conflicts'
  },
  {
    id: 2,
    name: 'Sarah Mitchell',
    role: 'VP of Operations',
    company: 'Construction Innovations Ltd',
    content: 'Managing $250M construction portfolio became effortless. The platform predicted delays 3 weeks in advance with 91% accuracy, budget variance decreased from 15% to 3%, and stakeholder satisfaction jumped 67%. Best project management tool we ever implemented.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=faces',
    industry: 'Construction',
    color: '#0FFFA0',
    impact: '91% delay prediction'
  },
  {
    id: 3,
    name: 'Michael Chen',
    role: 'Chief Strategy Officer',
    company: 'Enterprise Consulting Group',
    content: 'Portfolio optimization with AI recommendations improved ROI 156%. Resource utilization increased from 62% to 89%, cross-project dependencies tracked automatically, and strategic alignment improved 73%. The intelligent insights transformed our decision-making process.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces',
    industry: 'Consulting',
    color: '#8A3FFC',
    impact: '156% ROI improvement'
  },
  {
    id: 4,
    name: 'Jennifer Roberts',
    role: 'Head of Product Development',
    company: 'Innovation Labs Inc',
    content: 'Planfusion coordinated 12 product development teams seamlessly. Sprint planning time reduced 65%, velocity predictions within 5% accuracy, and time-to-market accelerated 38%. The AI-powered risk detection prevented 23 critical blockers. Game-changing platform.',
    rating: 5,
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=faces',
    industry: 'Product Development',
    color: '#FF3D9A',
    impact: '38% faster delivery'
  }
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      className="glass-strong w-[380px] h-full group hover:scale-[1.02] transition-all duration-300 relative flex-shrink-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="absolute top-0 left-0 right-0 h-1 transition-all duration-300"
        style={{ backgroundColor: testimonial.color }}
      />
      
      <motion.div
        className="absolute inset-0 opacity-0 transition-opacity duration-500 blur-xl pointer-events-none"
        animate={{ opacity: isHovered ? 1 : 0 }}
        style={{
          background: `radial-gradient(circle at top, ${testimonial.color}30, transparent 70%)`
        }}
      />

      <CardContent className="p-6 pb-8 relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full overflow-hidden ring-2 transition-all duration-300"
              style={{ borderColor: isHovered ? testimonial.color : 'rgba(255,255,255,0.2)' }}
            >
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h4 className="font-bold">{testimonial.name}</h4>
              <p className="text-xs text-muted-foreground">{testimonial.role}</p>
            </div>
          </div>
          <motion.div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${testimonial.color}20` }}
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.6 }}
          >
            <Quote className="w-4 h-4" style={{ color: testimonial.color }} />
          </motion.div>
        </div>

        <div className="mb-4">
          <p className="font-semibold text-sm">{testimonial.company}</p>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: `${testimonial.color}20`,
              color: testimonial.color
            }}
          >
            {testimonial.industry}
          </span>
        </div>

        <div className="flex gap-1 mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star
              key={i}
              className="w-4 h-4 fill-current"
              style={{ color: testimonial.color }}
            />
          ))}
        </div>

        <blockquote className="text-sm text-muted-foreground mb-4 flex-grow text-justify">
          "{testimonial.content}"
        </blockquote>

        <div className="flex items-center gap-2 pt-4 border-t border-white/10">
          <TrendingUp className="w-4 h-4" style={{ color: testimonial.color }} />
          <span className="text-xs font-semibold" style={{ color: testimonial.color }}>
            {testimonial.impact}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const MarqueeRow: React.FC<{
  testimonials: Testimonial[];
  direction?: 'left' | 'right';
  speed?: number;
}> = ({ testimonials, direction = 'left', speed = 40 }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const x = useMotionValue(0);
  const controls = useAnimationControls();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isMobile, isPaused, testimonials.length]);

  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];
  const totalDistance = testimonials.length * 386 * 2;

  useEffect(() => {
    if (isMobile) return;
    
    const startAnimation = async () => {
      const currentX = x.get();
      const targetX = direction === 'left' ? -totalDistance : 0;
      const startX = direction === 'left' ? 0 : -totalDistance;
      
      let progress: number;
      if (direction === 'left') {
        progress = Math.abs(currentX) / totalDistance;
      } else {
        progress = (totalDistance - Math.abs(currentX)) / totalDistance;
      }
      
      progress = progress % 1;
      const remainingTime = speed * (1 - progress);
      
      if (Math.abs(currentX) < 10 || progress > 0.99) {
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      } else {
        await controls.start({
          x: targetX,
          transition: {
            duration: remainingTime,
            ease: 'linear',
          },
        });
        
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      }
    };

    if (!isPaused) {
      startAnimation();
    } else {
      controls.stop();
    }

    return () => {
      controls.stop();
    };
  }, [isPaused, controls, x, speed, direction, totalDistance, isMobile]);

  if (isMobile) {
    return (
      <div className="relative px-4 ">
        <div className="flex justify-center items-center">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-[380px]"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="h-[380px]">
              <TestimonialCard testimonial={testimonials[currentIndex]} />
            </div>
          </motion.div>
        </div>
        
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-blue-500 w-8'
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden py-3 hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        className="flex gap-6"
        drag="x"
        dragConstraints={{ left: -4000, right: 100 }}
        dragElastic={0.1}
        dragMomentum={false}
        style={{ 
          x,
          cursor: 'grab',
          userSelect: 'none'
        }}
        animate={controls}
        onDragStart={() => setIsPaused(true)}
        onDragEnd={() => {
          setTimeout(() => setIsPaused(false), 500);
        }}
        whileTap={{ cursor: 'grabbing' }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div key={`${testimonial.id}-${index}`} className="h-[380px]">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const PlanfusionTestimonials: React.FC = () => {
  return (
    <section className="py-15 relative overflow-hidden hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
      
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.5, 0.3, 0.5],
        }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />
      
      <div className="max-w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10 px-4"
        >
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-blue-500 mr-3 animate-pulse" />
            <h2 className="mb-0">
              <span className="bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 bg-clip-text text-transparent">
                Project Excellence
              </span>{' '}
              Stories
            </h2>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            See how organizations transformed project delivery with AI-powered planning
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-0"
        >
          <MarqueeRow testimonials={planfusionTestimonials} direction="left" speed={65} />
        </motion.div>

        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent pointer-events-none z-20" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent pointer-events-none z-20" />
      </div>
    </section>
  );
};

const Planfusion: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Brain,
      title: 'Unified Lead Capture ',
      description: 'Automatically pulls leads from websites, landing pages, social campaigns, events, call logs, walk-ins, and referrals into one queue.'
    },
    {
      icon: Users,
      title: 'Smart Lead Scoring & Segmentation ',
      description: 'Scores leads on fit and intent, and groups them by industry, product interest, geography, and more.'
    },
    {
      icon: GitBranch,
      title: 'Automated Assignment & Workflows ',
      description: 'Routes leads to the right salesperson, branch, or partner based on rules such as region, product line, or deal size.'
    },
    {
      icon: BarChart3,
      title: '360° Activity Timeline',
      description: 'Tracks every call, email, meeting, note, and proposal so anyone can see what has happened and what’s next.'
    },
    {
      icon: MessageSquare,
      title: 'Follow-Up Reminders & Sequences ',
      description: 'Sends alerts and automated follow-up emails/SMS so no one forgets to call back.'
    },
    {
      icon: Target,
      title: 'Configurable Pipelines ',
      description: 'Lets you define sales stages for different products or business units and move leads through with simple drag-and-drop.'
    },
    {
      icon: CheckCircle,
      title: 'Configurable Pipelines  ',
      description: 'Lets you define sales stages for different products or business units and move leads through with simple drag-and-drop.'
    }
  ];

  const solutions = [
    {
      icon: FolderKanban,
      title: '	B2B & Industrial Sales',
      description: 'Manage complex deals with multiple stakeholders, track long sales cycles, and coordinate between pre-sales, sales, and technical teams from a single system.',
    },
    {
      icon: Calendar,
      title: '	Education & Training Institutions',
      description: 'Capture student and parent enquiries from fairs, agents, and campaigns; track them through counselling, applications, and admissions; and identify which channels bring the best-fit students',
    },
    {
      icon: PieChart,
      title: '	Real Estate & Property Services',
      description: 'Centralise leads from portals, brokers, and walk-ins; assign them to agents by project or location; and monitor site visits, negotiations, and booking status in real time.',
    },
    {
      icon: Network,
      title: 'Financial Services & Insurance',
      description: 'Route leads from branches, websites, and partners to advisors, enforce KYC/document workflows, and track policy or product conversions across regions.',
    },
    {
      icon: Star,
      title: 'Healthcare, Clinics & Wellness Brands',
      description: 'Manage patient enquiries for treatments and packages, coordinate follow-ups, and track conversion from enquiry to appointment and beyond—without losing context between departments.',
    }
  ];

  const compliances = [
    {
      icon: Zap,
      title: '	Role-Based Access Control (RBAC)',
      description: 'Different roles (agents, team leads, managers, partners) see only the leads, reports, and actions they are authorised to access.',
    },
    {
      icon: Eye,
      title: '	Secure Cloud Infrastructure ',
      description: 'Deployed on hardened cloud environments with regular patches, backups, and monitoring. ',
    },
    {
      icon: Users,
      title: '	Encryption in Transit & at Rest ',
      description: 'Lead details, communication history, and documents are protected using modern encryption standards.',
    },
    {
      icon: Award,
      title: '	Audit Trails & Activity Logs ',
      description: 'Key actions such as data edits, ownership changes, and exports are logged to support governance and dispute resolution.',
    },
    {
      icon: Calendar,
      title: '	Configurable Data Retention & Regional Policies ',
      description: 'Admins can define retention rules and data residency to align with company policies and local regulations.',
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: '	Higher Conversion Rates ',
      description: 'Every lead is captured, prioritised, and followed up systematically, which directly lifts win rates.',
    },
    {
      icon: Zap,
      title: '	Better Team Productivity ',
      description: 'Automation handles assignments, reminders, and data entry, freeing teams to focus on real conversations.',
    },
    {
      icon: Users,
      title: '	Clear Visibility for Management ',
      description: 'Dashboards show exactly what’s in the pipeline, where deals are stuck, and which channels and teams are performing.',
    },
    {
      icon: Eye,
      title: '	Consistent Client Experience ',
      description: 'Standardised workflows ensure prospects get timely, professional responses regardless of who takes the call.',
    },
    {
      icon: Bell,
      title: '	Smarter Growth Decisions  ',
      description: 'Reliable data on lead sources and conversion helps you invest in the right markets, products, and campaigns.',
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-orange-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 neon-glow">
                  <Layers className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-orange-400 uppercase tracking-wide">Project Planning & Management</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-orange-500 to-amber-600 bg-clip-text text-transparent">Planfusion</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">AI-Powered Project Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
                The Lead Management System is a central hub for capturing, tracking, and nurturing every enquiry your organization receives. Instead of scattered spreadsheets and missed follow-ups, your team gets a single, structured view of every prospect and where they are in the journey. From the first website form fill to the signed contract, the system makes sure each interaction is logged, owned, and followed up on time. Sales, marketing, and service teams work off the same live data, which means fewer surprises, smoother handovers, and more predictable revenue.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-amber-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
                >
                  Start Planning Smarter
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-500">40%</div>
                  <div className="text-sm text-muted-foreground">More On-Time</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-400">60%</div>
                  <div className="text-sm text-muted-foreground">Faster Plans</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">AI</div>
                  <div className="text-sm text-muted-foreground">Powered</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/planfusion.png"
                    alt="Planfusion Project Management Platform"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Complete project planning and resource management toolkit
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Planning Capabilities Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
               <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive tools for every project management methodology
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{solution.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{solution.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              AI-powered intelligence for project success
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-orange-500/50 hover:border-orange-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-orange-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Measurable improvements in project delivery
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-orange-500/50 hover:border-orange-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-orange-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{benefit.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <PlanfusionTestimonials />

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-orange-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-amber-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">Project Management?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-justify">
                Discover how Planfusion can help your teams deliver projects faster, optimize resource utilization, and achieve strategic goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-orange-500/30 hover:border-orange-500/50 transition-all duration-300 text-white"
                >
                  Schedule Demo
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Planfusion;